/**
 * Конфигурация приложения app-web
 * 
 * Чувствительные данные импортируются из переменных окружения (REACT_APP_*).
 * Не-чувствительные настройки хранятся в этом config.ts файле.
 * 
 * Для продакшена используйте переменные окружения в .env.production
 */

/**
 * Основной объект конфигурации приложения
 */
export const config = {
  /**
   * Настройки API
   */
  api: {
    /**
     * Базовый URL API.
     *
     * По умолчанию относительный путь `/api`, чтобы запросы всегда шли на тот
     * же домен, с которого открыт сайт (browser подставит текущий origin).
     * Это избавляет от жёсткой привязки к конкретному домену (например,
     * https://ok-telecom.ru) и корректно работает за reverse-proxy на любом хосте.
     *
     * Задавайте `REACT_APP_API_URL` только если API расположен на ОТДЕЛЬНОМ
     * домене (кросс-доменный сценарий) — тогда укажите полный URL, например
     * `https://api.example.com/api`.
     */
    baseUrl: process.env.REACT_APP_API_URL || '/api',
  },

  /**
   * Настройки приложения
   */
  app: {
    port: parseInt(process.env.PORT || '3010'),
    publicUrl: process.env.PUBLIC_URL || 'http://localhost:3010',
    nodeEnv: process.env.NODE_ENV || 'development',
  },

  /**
   * Информация о компании (не-чувствительные данные)
   */
  company: {
    name: 'ОК-Телеком',
    phone: '8-800-222-10-79', // Публичный номер техподдержки
    email: 'support@ok-telecom.ru', // Публичный email
    telegram: {
      // Ссылка и имя Telegram-бота. Берутся из переменных окружения, с
      // фоллбэком на дефолтного бота, чтобы блок всегда отображался.
      bot: process.env.TELEGRAM_BOT || 'https://t.me/okTelecomDevBot',
      botName: process.env.TELEGRAM_BOT_NAME || '@okTelecomDevBot',
    },
    // Адрес офиса (публичная информация)
    address: 'г. Москва, ул. Примерная, д. 1',
    // Режим работы техподдержки
    supportHours: '24/7',
  },

  /**
   * Настройки React Query
   */
  reactQuery: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 минут
      },
    },
  },

  /**
   * Настройки для продакшена
   */
  production: {
    enableAnalytics: process.env.NODE_ENV === 'production',
    enableErrorReporting: process.env.NODE_ENV === 'production',
    // Дополнительные настройки для продакшена
    enableServiceWorker: process.env.NODE_ENV === 'production',
    enableCompression: process.env.NODE_ENV === 'production',
  },

  /**
   * Настройки производительности
   */
  performance: {
    // Включить lazy loading для изображений
    enableLazyLoading: process.env.NODE_ENV === 'production',
    // Включить code splitting
    enableCodeSplitting: process.env.NODE_ENV === 'production',
    // Размер chunk'а для code splitting (в KB)
    chunkSizeLimit: parseInt(process.env.REACT_APP_CHUNK_SIZE_LIMIT || '244'),
  },

  /**
   * Настройки localStorage
   */
  storage: {
    keys: {
      // Ключ для хранения данных формы оплаты
      paymentFormData: 'app-web-payment-data',
      // Ключ для хранения токена авторизации
      authToken: 'app-web-auth-token',
      // Ключ для хранения данных пользователя
      userData: 'app-web-user-data',
    },
    // TTL для данных оплаты (30 дней)
    paymentDataTTL: 30 * 24 * 60 * 60 * 1000,
  },

  /**
   * Настройки оплаты
   */
  payment: {
    minAmount: 100,
    maxAmount: 15000,
    /**
     * Лимит суммы платежа с меткой годовой оплаты (₽).
     * Должен совпадать с backend `config.publicPayment.annualMaxAmount`.
     */
    annualMaxAmount: 100000,
    /**
     * Допустимый формат номера лицевого счёта в query-параметре:
     * только цифры, длина 4–20. Контрольная цифра не проверяется,
     * т.к. у мигрированных номеров другая длина.
     */
    accountNumberPattern: /^\d{4,20}$/,
    currency: 'RUB',
    currencySymbol: '₽',
  },

  /**
   * Настройки валидации
   */
  validation: {
    phone: {
      pattern: /^\+7\d{10}$/,
      mask: '+7 (___) ___-__-__',
      placeholder: '+7 (900) 123-45-67',
    },
    accountNumber: {
      pattern: /^\d{8}$/,
      length: 8,
      placeholder: '12345678',
    },
    password: {
      minLength: 6,
      maxLength: 50,
    },
  },

  /**
   * Настройки UI
   */
  ui: {
    // Задержка для debounce при вводе
    debounceDelay: 300,
    
    // Время показа уведомлений
    notificationDuration: 5000,
    
    // Breakpoints (соответствуют MUI)
    breakpoints: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
};

// Экспорт для обратной совместимости
export const API_URL = config.api.baseUrl;
export const PORT = config.app.port;
export const PUBLIC_URL = config.app.publicUrl;
export const TELEGRAM_BOT = config.company.telegram.bot;
export const TELEGRAM_BOT_NAME = config.company.telegram.botName;
export const REACT_QUERY_CONFIG = config.reactQuery;
